import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import useTranslation from 'next-translate/useTranslation';

// PropTypes
import { jobPropTypes } from '../../../../proptypes/jobProptypes';
import { companyPropTypes } from '../../../../proptypes/companyProptypes';

// Context
import ApproachContext from '../../../../context/ApproachContext';

// Components
import VideoPlayer from '../../VideoPlayer';
import ImageGallery from '../../ImageGallery';

// Utils
import { langShorthand } from '../../../../utils';

// Styles
import styles from './aboutCompanySection.module.scss';

const AboutCompanySection = ({
  job,
  company,
  showImageCarousel,
  setShowImageCarousel,
}) => {
  const approach = useContext(ApproachContext);
  const { t } = useTranslation('common');

  const calculateRatingStars = (ratingString) => {
    const ratingIcons = [];
    const rating = Number(ratingString);
    const integerRating = Math.ceil(rating);
    const ratingFraction = rating % 1;

    for (let i = 1; i <= integerRating; i++) {
      if (Math.floor(rating) >= i) {
        ratingIcons.push(
          <img
            key={Math.random()}
            src="/static/star-full.svg"
            alt="star icon full"
            className={styles.starIcon}
          />,
        );
      } else if (Math.floor(rating) < i && Math.ceil(rating) < i) {
        ratingIcons.push(
          <img
            key={Math.random()}
            src="/static/star-empty.svg"
            alt="star icon empty"
            className={styles.starIcon}
          />,
        );
      } else if (ratingFraction <= 0.201) {
        // adding 0.x01 because of JS number precision
        ratingIcons.push(
          <img
            key={Math.random()}
            src="/static/star-empty.svg"
            alt="star icon empty"
            className={styles.starIcon}
          />,
        );
      } else if (ratingFraction <= 0.701) {
        ratingIcons.push(
          <img
            key={Math.random()}
            src="/static/star-half.svg"
            alt="star icon half"
            className={styles.starIcon}
          />,
        );
      } else {
        ratingIcons.push(
          <img
            key={Math.random()}
            src="/static/star-full.svg"
            alt="star icon full"
            className={styles.starIcon}
          />,
        );
      }
    }

    return ratingIcons;
  };

  if (!job.extra_info?.can_be_disclosed) return null;

  return (
    <section id="about-company-section" className={styles.container}>
      <h2 className={styles.heading}>
        {t(`description_page.about_company_section.title.${approach}`, {
          company_name: company.company_name,
        })}
      </h2>
      <div
        className={`html-div ${styles.content}`}
        dangerouslySetInnerHTML={{
          __html: company.description?.find(
            (c) => c.lang === langShorthand(job.job_language.mid),
          )?.content,
        }}
      />
      <div className={styles.listContainer}>
        <ul className={styles.list}>
          {company.industry_object && (
            <li>
              {t(`description_page.about_company_section.industry.${approach}`)}
              <span className={styles.separator}>:</span>{' '}
              {
                company.industry_object.display[
                  langShorthand(job.job_language.mid)
                ]
              }
            </li>
          )}

          {company.founded && (
            <li>
              {t(`description_page.about_company_section.founded.${approach}`)}
              <span className={styles.separator}>:</span> {company.founded}
            </li>
          )}

          {company.kununu_rating && Number(company.kununu_rating) >= 4 && (
            <li>
              Kununu
              <span className={styles.separator}>:</span>{' '}
              {calculateRatingStars(company.kununu_rating)}
            </li>
          )}

          {company.glassdoor_rating && Number(company.glassdoor_rating) >= 4 && (
            <li>
              Glassdoor
              <span className={styles.separator}>:</span>{' '}
              {calculateRatingStars(company.glassdoor_rating)}
            </li>
          )}
        </ul>
      </div>

      {company.company_video && (
        <div className={styles.video}>
          <VideoPlayer url={company.company_video} />
        </div>
      )}

      {company.pictures?.length > 0 && (
        <div className={styles.pictures}>
          <ImageGallery
            images={company.pictures}
            showFullscreen={showImageCarousel}
            setShowFullscreen={setShowImageCarousel}
          />
        </div>
      )}
    </section>
  );
};

AboutCompanySection.propTypes = {
  job: PropTypes.shape(jobPropTypes).isRequired,
  company: PropTypes.shape(companyPropTypes).isRequired,
  showImageCarousel: PropTypes.bool.isRequired,
  setShowImageCarousel: PropTypes.func.isRequired,
};

export default AboutCompanySection;
